import classnames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { listerClickThrough } from '../../../actions/lister-actions';
import { useEventListener } from '../../../hooks/useEventListener';
import { useIsProductInHighDemand } from '../../../hooks/useIsProductInHighDemand';
import { useTheme } from '../../../hooks/useTheme';
import TRANSLATIONS from '../../../translations/translations@default.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
} from '../../../utilities/lister-utilities';
import { currencyFormatter } from '../../../utilities/number-utilities';
import { Context } from '../../Views/store/store';
import AOWeRecommendSmile from '../AOWeRecommendSmile';
import AddToBasketButton from '../AddToBasket/AddToBasketButton';
import EnergyInformation from '../EnergyInformation/EnergyInformation';
import FinanceProposition from '../FinanceProposition';
import Heading from '../Heading';
import HighDemandFlag from '../HighDemand/HighDemandFlag';
import HighDemandMessaging from '../HighDemand/HighDemandMessaging';
import MobileDeliveryMessageBlock from '../MobileProductCard/MobileDeliveryMessage/MobileDeliveryMessageBlock@default';
import MoreInfo from '../MoreInfo';
import OutOfStock from '../OutOfStock/OutOfStock';
import PricePod from '../PricePod/PricePod@default';
import ProductImage from '../ProductImage/ProductImage';
import ReviewRating from '../ReviewRating/ReviewRating';
import DealsMobileSkeleton from './DealsMobileSkeleton';

const formatter = currencyFormatter(TRANSLATIONS.general);

export const DealsMobileProductCard = ({ index, product, observer, page }) => {
	const card = useRef(null);

	useEffect(() => {
		const cardRef = card.current;

		if (cardRef && observer) {
			observer.observe(cardRef);
		}

		return () => {
			if (observer) {
				observer.unobserve(cardRef);
			}
		};
	}, []);

	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	const dispatch = useDispatch();

	const isSkuHighDemand = useIsProductInHighDemand(product);

	const theme = useTheme('weRecommend');

	if (!loaded) {
		return <DealsMobileSkeleton product={product} card={card} page={page} />;
	}

	const showFeaturedProduct = index === 0 && product.IsFeaturedProduct;

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);

	const was =
		product.PricePodViewModel.WasPrice ||
		product.PricePodViewModel.RetailPriceIncVat;

	const now = product.PricePodViewModel.NowPrice;

	let saving = !isNaN(was) && !isNaN(now) ? Math.floor(was - now) : 0;

	return (
		<li className="p-2 pt-0 md:w-1/2" id={product.Code}>
			<div
				ref={card}
				data-page={page}
				className={classnames('relative flex flex-col h-full', {
					[`overflow-hidden rounded-md pb-2 px-2 ${theme.background}`]:
						index === 0 && showFeaturedProduct,
				})}>
				{index === 0 && showFeaturedProduct && (
					<React.Fragment>
						<div className={`text-title py-2 px-1 ${theme.text}`}>
							{TRANSLATIONS.productList.productCard.featuredProductFlag}
						</div>

						<AOWeRecommendSmile
							className="absolute"
							style={{ top: '-170px', left: '-110px' }}
							fill={theme.fill}
						/>
					</React.Fragment>
				)}

				<a
					href={product.FullProductUrl}
					onClick={(event) => {
						event.preventDefault();
						window.location.assign(product.FullProductUrl);

						dispatch(listerClickThrough(product.Code));
					}}
					className="deals-product__link absolute inset-0 z-10"
					itemProp="url"
					data-testid="product-card-link"
					data-tag-name="lister product card"
					data-tag-type={
						index === 0 && showFeaturedProduct ? 'featured' : 'standard'
					}
					data-tag-delivery-date={deliveryDate}
					data-tag-delivery-days={deliveryDaysDifference}>
					<span className="block">
						{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
					</span>
				</a>

				<div className="deals-product relative flex flex-col bg-white rounded-lg border border-solid border-gray-30 p-4 pt-3 h-full">
					{(product.State === 'OutOfStock' || isSkuHighDemand) && (
						<div className="flex pb-2">
							{isSkuHighDemand && product.State !== 'OutOfStock' && (
								<HighDemandFlag />
							)}
							{product.State === 'OutOfStock' && <OutOfStock deals />}
						</div>
					)}
					{!isSkuHighDemand && product.State !== 'OutOfStock' && (
						<div className="flex pb-2 justify-between">
							<div className="deals-promo-title flex justify-center items-center whitespace-no-wrap">
								<div className="bg-error flex rounded-xs h-10 items-center justify-center leading-none mr-2 p-3 w-full text-title-sm text-error">
									{saving > 0 ? (
										<React.Fragment>
											{TRANSLATIONS.productList.productCard.deals.save}
											<span className="ml-1 text-error text-title">
												{formatter.format(saving)}
											</span>
										</React.Fragment>
									) : (
										TRANSLATIONS.productList.productCard.deals.dealsFlag
									)}
								</div>
							</div>
							{/* <div className="flex items-center">
								<PromoFlag
									{...product}
								/>
							</div> */}
						</div>
					)}
					<div className="flex h-full">
						<div className="deals-product__image-side flex flex-col w-1/2 mr-2">
							<ProductImage {...product} />

							{product.EnergyRating && (
								<div className="py-2 flex justify-center">
									<EnergyInformation
										energyRatingDetails={product.EnergyRatingDetails}
									/>
								</div>
							)}
							{!isSkuHighDemand && (
								<MobileDeliveryMessageBlock
									deliveryMessage={product.DynamicDeliveryBulletsViewModel[0]}
									stockStatus={product.State}
									deals
								/>
							)}
						</div>
						<div className="deals-product__title-side flex flex-col justify-between w-1/2">
							<div>
								<Heading content={product.FullTitle} />
								<PricePod viewModel={product.PricePodViewModel} />
								<ReviewRating {...product} />
								<FinanceProposition {...product} />
							</div>
							<div className="pt-2">
								<MoreInfo {...product} />
								{product.State !== 'OutOfStock' && !isSkuHighDemand && (
									<AddToBasketButton {...product} />
								)}
							</div>
						</div>
					</div>

					<div className="mt-1">
						{isSkuHighDemand && product.State !== 'OutOfStock' && (
							<HighDemandMessaging />
						)}
					</div>
				</div>
			</div>
		</li>
	);
};

export default DealsMobileProductCard;
